import Link from 'next/link';
import Image from 'next/image';

import { AnchorArrow } from 'react-scale/components';
import Tabs from 'react-scale/components/ui/tabs';

import styles from './IndexPage.module.css';


const IndexPage = () => (
  <div>
    <header className="flex items-center justify-between px-2 py-8 -mx-2 bg-gray-100 border-b border-gray-200 md:py-0 lg:bg-white dark:bg-gray-900 lg:px-0 lg:mx-0 sm:py-8 dark:border-gray-800">
      <div>
        <h2 className="mb-4 text-2xl text-black font-aeonik md:text-4xl dark:text-white">
          Documentation
        </h2>
        <p className="text-base text-gray-800 md:text-lg dark:text-gray-500">
          Guides and quickstarts for integrating Scale products.
        </p>
      </div>
      <div className="hidden pl-2 lg:pl-0 md:py-8 lg:pr-5 md:block">
        <Image src="/static/images/illustrations/labeling-white.svg" width="200" height="221" />
      </div>
    </header>
    <div className="mt-10 mb-8">
      <p className="mb-4 text-base text-gray-700 dark:text-gray-300">
        Scale AI’s mission is to accelerate the development of AI applications. To enable teams to
        make faster progress, we began with data - the foundation of all AI applications. Scale AI
        turns raw data into high-quality training data by combining machine learning powered
        pre-labeling and active tooling with varying levels and types of human review.
      </p>
      <p className="mb-8 text-base text-gray-700 dark:text-gray-300">
        That’s why we provide two tiers for our customers to use our platform:
      </p>
      <div className="grid gap-4 mb-10 md:grid-cols-2">
        <div className="px-8 py-6 transition-shadow duration-300 ease-in-out bg-white border border-gray-300 dark:bg-gray-900 dark:border-gray-700 hover:shadow-2xl">
          <Link href="https://dashboard.scale.com/signup">
            <a className="font-normal">
              <div>
                <p className="flex items-center mb-4 text-lg text-black dark:text-white">
                  <img
                    className="h-6 mr-2"
                    src="/static/img/website/big/icons/pricing/rapid.svg"
                    alt="rapid"
                  />
                  Rapid
                </p>
                <p className="mb-6 text-sm text-gray-700 dark:text-gray-400">
                  Our Scale Rapid model requires no long-term commitment and is a pay-as-you-go
                  model. This is ideal for projects with lower volumes with production level
                  quality and quick turnarounds.
                </p>
                <p className="mt-auto">
                  <AnchorArrow
                    href="/rapid"
                    text="Get Started Today"
                  />
                </p>
              </div>
            </a>
          </Link>
        </div>
        <div className="px-8 py-6 transition-shadow duration-300 ease-in-out bg-white border border-gray-300 dark:bg-gray-900 dark:border-gray-700 hover:shadow-2xl">
          <Link href="/sales">
            <a className="font-normal">
              <div className="flex flex-col h-full">
                <p className="flex items-center mb-4 text-lg text-black dark:text-white">
                  <img
                    className="h-6 -ml-2"
                    src="/static/img/website/big/icons/section-title/enterprise-ready.svg"
                    alt="enterprise"
                  />
                  Enterprise
                </p>
                <p className="mb-2 text-sm text-gray-700 dark:text-gray-400">
                  Our Scale Enterprise model has annual commitments with volume-based rates. This
                  is a deal for strategic AI initiatives with guaranteed turnaround times and
                  quality.
                </p>
                <p className="mt-auto">
                  <AnchorArrow
                    href="/sales"
                    text="Talk to Us"
                  />
                </p>
              </div>
            </a>
          </Link>
        </div>
      </div>
      <p className="text-sm text-center text-gray-700 dark:text-gray-300">
        More information on both models can be found on our{' '}
        <Link href="/pricing">
          <a className="text-link">pricing page</a>
        </Link>
        .
      </p>
    </div>
    <div className="py-16 mt-16 border-t border-gray-300 dark:border-gray-800">
      <h2 className="mb-2 text-2xl text-black dark:text-white">Ready to get started?</h2>
      <p className="text-base text-gray-700 dark:text-gray-300">
        Follow the 4 steps below and you’ll be well on your way to building a robust training
        dataset for your next AI application.
      </p>
    </div>
    <div className={styles.tabContent}>
      <Tabs tabs={valuesTabs} />
    </div>
    <div className="pt-16 mt-16 border-t border-gray-300 dark:border-gray-800">
      <h2 className="mb-4 text-2xl text-black dark:text-white">Need Help?</h2>
      <p className="mb-4 text-base text-gray-700 dark:text-gray-300">
        If you have any questions, feel free to email us at{' '}
        <a className="text-link" href="mailto:support@scale.com">support@scale.com</a>.
      </p>
      <p className="text-base text-gray-700 dark:text-gray-300">
        If you are interested in learning more about our Enterprise engagements,{' '}
        <Link href="/sales">
          <a className="text-link">get in touch with us</a>
        </Link>
        .
      </p>
    </div>
    <div className="pb-40" />
  </div>
);

const LabelingTask = () => (
  <div className="-mt-10 text-base text-gray-700 dark:text-gray-300">
    <p className="mb-8">
      Scale offers high quality training and validation data for AI applications. Both Scale Rapid
      and Enterprise customers have access to:
    </p>
    <div className="grid gap-16 md:grid-cols-2">
      <Link href="/image">
        <a className="flex gap-x-4">
          <div className="flex-shrink-0">
            <Image
              src="/static/images/icons/products/image.svg"
              width={56}
              height={56}
              alt="Image Product"
              quality={100}
            />
          </div>
          <div>
            <img src="/static/images/logos/logo-true-gray-400.svg" alt="Scale Logo" className="h-4" />
            <h2 className="mt-2 text-2xl font-aeonik">Image</h2>
          </div>
        </a>
      </Link>
      <Link href="/3d-sensor-fusion">
        <a className="flex gap-x-4">
          <div className="flex-shrink-0">
            <Image
              src="/static/images/icons/products/3d-sensor-fusion.svg"
              width={56}
              height={56}
              alt="3d sensor fusion"
              quality={100}
            />
          </div>
          <div>
            <img
              src="/static/images/logos/logo-true-gray-400.svg"
              alt="Scale Logo"
              className="h-4"
            />
            <h2 className="mt-2 text-2xl font-aeonik">3D Sensor Fusion</h2>
          </div>
        </a>
      </Link>
      <Link href="/video">
        <a className="flex gap-x-4">
          <div className="flex-shrink-0">
            <Image
              src="/static/images/icons/products/video.svg"
              width={56}
              height={56}
              alt="Video Product"
              quality={100}
            />
          </div>
          <div>
            <img
              src="/static/images/logos/logo-true-gray-400.svg"
              alt="Scale Logo"
              className="h-4"
            />
            <h2 className="mt-2 text-2xl font-aeonik">Video</h2>
          </div>
        </a>
      </Link>
      <Link href="/text">
        <a className="flex gap-x-4">
          <div className="flex-shrink-0">
            <Image
              src="/static/images/icons/products/text.svg"
              width={56}
              height={56}
              alt="Text Product"
              quality={100}
            />
          </div>
          <div>
            <img
              src="/static/images/logos/logo-true-gray-400.svg"
              alt="Scale Logo"
              className="h-4"
            />
            <h2 className="mt-2 text-2xl font-aeonik">Text</h2>
          </div>
        </a>
      </Link>
      <Link href="/document-ai">
        <a className="flex gap-x-4">
          <div className="flex-shrink-0">
            <Image
              src="/static/images/icons/products/document.svg"
              width={56}
              height={56}
              alt="Document AI Product"
              quality={100}
            />
          </div>
          <div>
            <img
              src="/static/images/logos/logo-true-gray-400.svg"
              alt="Scale Logo"
              className="h-4"
            />
            <h2 className="mt-2 text-2xl font-aeonik">Document AI</h2>
          </div>
        </a>
      </Link>
    </div>
  </div>
);

const DesignTask = () => (
  <div className="-mt-10 text-base text-gray-700 dark:text-gray-300">
    <p className="mb-4">
      Writing clear and concise instructions is <strong>crucial</strong> to receiving high-quality
      training data with Scale.{' '}
    </p>
    <p className="mb-4">
      We strongly encourage you to use the following{' '}
      <a className="text-link" href="https://docs.google.com/document/d/1EVbckrKjWFsS0C-xjd0bm62CpO6sgjKfuLyUEZa7VIU/edit?usp=sharing">
        template
      </a>{' '}
      to get started.{' '}
    </p>
    <p className="mb-4">Clear and concise instructions should include:</p>
    <ul>
      <li className="mb-2">Step-by-step directions</li>
      <li className="mb-2">
        Rules and example images (i.e. minimum label size, when to not label an object)
      </li>
      <li className="mb-2">
        Label classes and attributes / fields with definitions and example images
      </li>
      <li className="mb-2">Screenshots of correct and incorrect tasks for comparison</li>
    </ul>
    <p className="mb-2">To embed your google docs instructions into your tasks:</p>
    <ol>
      <li className="mb-2">
        In your Google Docs, click File → Publish To Web → Embed (in the window that appears)
      </li>
      <li className="mb-2">
        Copy the iFrame tag that’s provided:{' '}
        <code>&lt;iframe src=&quot;[YOUR_CUSTOM_DOC_LINK]&quot;&gt;&lt;/iframe&gt;</code>
      </li>
      <li className="mb-2">
        Paste the iFrame tag in the instruction parameter on the task, or paste it into your project
        instructions in the UI.
      </li>
    </ol>
    <p className="mb-4">
      We have created detailed example instructions and guides{' '}
      <a href="https://scale.com/docs/instructions">on our instructions page</a>.
    </p>
    <figure>
      <a className="text-link" href="https://files.readme.io/e578118-Template_Instructions.png">
        <img
          className="w-full overflow-hidden shadow-xl"
          src="https://files.readme.io/e578118-Template_Instructions.png"
          alt="design instruction"
        />
      </a>
    </figure>
  </div>
);

const DataPipeline = () => (
  <div className="-mt-10 text-base text-gray-700 dark:text-gray-300">
    <p className="mb-4">
      Once you have explored your specified task type and designed your task, write your REST API
      calls following our <strong>API documentation</strong>. We have examples in cURL, JavaScript,
      Python, and Ruby. You can submit your API calls via Terminal or a 3rd-party software like{' '}
      <a className="text-link" href="https://www.postman.com">Postman</a>.
    </p>
    <figure>
      <a href="https://files.readme.io/bee2298-Key_Concepts.gif">
        <img
          className="w-full overflow-hidden shadow-xl"
          src="https://files.readme.io/bee2298-Key_Concepts.gif"
          alt="data instruction"
        />
      </a>
    </figure>
  </div>
);

const IterateLearn = () => (
  <div className="-mt-10 text-base text-gray-700 dark:text-gray-300">
    <p className="mb-4">After you submit your first batch, you can explore the web application:</p>
    <ul>
      <li className="mb-2">
        <strong>
          <a href="https://scale.com/docs/overview-tab">Overview tab</a>
        </strong>
        : View a summary of your submissions over time and the status for each task type.
      </li>
      <li className="mb-2">
        <strong>
          <a href="https://scale.com/docs/tasks-overview">Tasks tab</a>
        </strong>
        : View a more granular list of your submitted tasks, filterable by status, project, and
        batch.
      </li>
      <li className="mb-2">
        <strong>
          <a href="https://scale.com/docs/quality-overview">Quality tab</a>
        </strong>
        : Audit a random sample of tasks for quality and view stats for tasks you’ve reviewed
      </li>
    </ul>
    <p className="mb-4">
      Submitting your projects to Scale should be an iterative process. After your first batch,
      please summarize common errors or specify new requirements in the instructions document. Once
      you have satisfied with the quality, feel free to increase the batch volume
    </p>
    <figure>
      <a href="https://files.readme.io/3a95d47-Iterate_and_Learn.gif">
        <img
          className="w-full overflow-hidden shadow-xl"
          src="https://files.readme.io/3a95d47-Iterate_and_Learn.gif"
          alt="iterate instruction"
        />
      </a>
    </figure>
  </div>
);

export const valuesTabs = [
  {
    title:
      <span>
        <span className="hidden md:block">Choose your Labeling Task</span>
        <span className="md:hidden">Choose</span>
      </span>,
    content: <LabelingTask />
  },
  {
    title:
      <span>
        <span className="hidden md:block">Design your Task</span>
        <span className="md:hidden">Design</span>
      </span>,
    content: <DesignTask />
  },
  {
    title:
      <span>
        <span className="hidden md:block">Set up your Data Pipeline</span>
        <span className="md:hidden">Setup</span>
      </span>,
    content: <DataPipeline />
  },
  {
    title:
      <span>
        <span className="hidden md:block">Iterate and Learn</span>
        <span className="md:hidden">Iterate</span>
      </span>,
    content: <IterateLearn />
  }
];

export default IndexPage;
